import { Headings } from '../../Headings';
import { Container } from '../../Layout';
import { Link } from '../../Link';
import { List } from '../../List';
import { Text } from '../../Text';
import { HtmlToJsxParserStyleParamsType } from './types/HtmlToJsxParserStyleParamsType';

const defaultStyleValues: HtmlToJsxParserStyleParamsType = {
  fontSize: 'standard',
  p: {
    marginBottom: 8,
  },
  h2: {
    marginBottom: 10,
  },
  h3: {
    marginBottom: 10,
  },
  h4: {
    marginBottom: 0,
  },
  list: {
    marginBottom: 20,
    marginTop: 20,
    itemSpacing: 8,
  },
};
const lastChildStyles: HtmlToJsxParserStyleParamsType = {
  p: {
    marginBottom: 0,
  },
  list: {
    marginBottom: 0,
  },
};

export const htmlToJsxParser = (element: string, styling?: HtmlToJsxParserStyleParamsType) => {
  if (!element) return null;
  let style = mergeDeep(defaultStyleValues, styling);
  const parser = new DOMParser();
  const doc = parser.parseFromString(element.replace(/\n/g, ''), 'text/html');
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    return null;
  }
  const children = Array.from(doc.body.childNodes);

  // Notes on type asserting as Element:
  // the doc.body.children prop which returns Elements[] does not contain text elements,
  // while .childNodes does include elements, but returns ChildNode[]
  // ChildNode type does not contain innerHTML, but the data is there
  const elements = children.map((node, index): JSX.Element | string | null => {
    if (index === children.length - 1) style = mergeDeep(style, lastChildStyles);

    switch (node.nodeName) {
      case '#text':
        return node.textContent?.trim().length ? node.textContent : null;
      case 'STRONG':
        return (
          <Text key={index} size={style.fontSize} renderAs="span" fontWeight="semibold">
            {htmlToJsxParser((node as Element).innerHTML, style)}
          </Text>
        );
      case 'SPAN':
        return (
          <Text key={index} size={style.fontSize} renderAs="span">
            {htmlToJsxParser((node as Element).innerHTML, style)}
          </Text>
        );
      case 'EM':
        return (
          <Text key={index} size={style.fontSize} renderAs="span" fontStyle="italic">
            {htmlToJsxParser((node as Element).innerHTML, style)}
          </Text>
        );
      case 'A':
        return (
          <Link
            key={index}
            fontSize={style.fontSize}
            to={(node as Element).attributes.getNamedItem('href')?.nodeValue || ''}
          >
            {htmlToJsxParser((node as Element).innerHTML, style)}
          </Link>
        );
      case 'H2':
        return (
          <Headings.H2 key={index} marginBottom={style.h2?.marginBottom}>
            {node.textContent}
          </Headings.H2>
        );
      case 'H3':
        return (
          <Headings.H3 key={index} marginBottom={style.h3?.marginBottom}>
            {node.textContent}
          </Headings.H3>
        );
      case 'H4':
        return (
          <Headings.H4 key={index} marginBottom={style.h4?.marginBottom}>
            {node.textContent}
          </Headings.H4>
        );
      case 'H5':
        return (
          <Headings.H5 key={index} marginBottom={style.h5?.marginBottom}>
            {node.textContent}
          </Headings.H5>
        );
      case 'DIV':
        return <Container key={index}>{htmlToJsxParser((node as Element).innerHTML, style)}</Container>;
      case 'P':
        return (
          <Text key={index} size={style.fontSize} marginBottom={style.p?.marginBottom}>
            {htmlToJsxParser((node as Element).innerHTML, style)}
          </Text>
        );
      case 'UL':
      case 'OL':
        return (
          <List
            key={index}
            bullet="dot"
            itemSpacing={style.list?.itemSpacing}
            marginBottom={style.list?.marginBottom}
            marginTop={style.list?.marginTop}
          >
            {Array.from(node.childNodes).map((liNode, liIndex) =>
              liNode.textContent?.trim().length ? (
                <List.Item key={liIndex}>
                  <List.Description fontSize={style.fontSize}>
                    {htmlToJsxParser((liNode as Element).innerHTML, style)}
                  </List.Description>
                </List.Item>
              ) : null
            )}
          </List>
        );

      default:
        return null;
    }
  });

  return elements.filter(e => e !== null);
};

/**
 * Deep merges two objects, overwriting the source object with the override object properties.
 * like { ...source, ...override } but recursively
 */
const mergeDeep = (source: { [key: string]: any }, override: { [key: string]: any } | undefined) => {
  const ret = { ...source };
  if (override) {
    for (const [key, value] of Object.entries(override)) {
      ret[key] = typeof value === 'object' ? mergeDeep(source[key] ?? {}, value) : value;
    }
  }
  return ret;
};
